import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Zap, ShieldCheck } from "lucide-react";
import { QuoteProcess } from "../components/custom-order/QuoteProcess";
import { ToggleCard } from "../components/custom-order/ToggleCard";
import { ProjectFields } from "../components/custom-order/ProjectFields";
import { OrderTotal } from "../components/custom-order/OrderTotal";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { designAssets } from "../lib/designAssets";

const STICKER_PRICE = { "sticker-10": 20, "sticker-50": 55, "sticker-100": 85 };
const RUSH_FEE = 30;
const MIL_PCT = 0.10;

export default function CustomOrder() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const tierParam = params.get("tier") || "custom-quote";
  const isSticker = tierParam.startsWith("sticker-");

  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "", shipping_address: "",
    material: isSticker ? "Vinyl" : "Walnut",
    description: "", quantity: 1, tier: tierParam,
    rush: false, military_discount: false,
    complexity: "standard", desired_deadline: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Sticker totals
  const stickerBase = isSticker ? (STICKER_PRICE[form.tier] || 25) : 0;
  const rushAdd = isSticker && form.rush ? RUSH_FEE : 0;
  const subtotal = stickerBase + rushAdd;
  const militaryOff = form.military_discount ? Math.round(subtotal * MIL_PCT) : 0;
  const total = subtotal - militaryOff;

  useEffect(() => {
    if (user) setForm(f => ({ ...f, email: f.email || user.email, customer_name: f.customer_name || user.name || "" }));
  }, [user]);

  const design = React.useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("ezjem_design") || "null"); } catch (e) { console.error("Stored design unreadable", e); return null; }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to continue"); nav("/login"); return; }
    setSubmitting(true);
    try {
      const { preview_image, ...rest } = design || {};
      const payload = { ...form, design_data: rest, design_preview_image: preview_image || window.__ezjem_preview || null };
      const { data: order } = await api.post("/orders", payload);
      if (designAssets.originals.length) {
        const results = await Promise.allSettled(designAssets.originals.map((f) => api.post(`/orders/${order.order_id}/assets`, f)));
        if (results.some((r) => r.status === "rejected")) toast.error("Some original files could not be attached — you can email them to us.");
        else designAssets.originals = [];
      }
      if (isSticker) {
        toast.success("Order created. Redirecting to secure payment…");
        const { data: co } = await api.post("/payments/checkout", { order_id: order.order_id, tier: form.tier, origin_url: window.location.origin });
        window.location.href = co.checkout_url;
      } else {
        toast.success("Quote request received — redirecting…");
        nav(`/quote-received/${order.order_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit order");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="custom-order-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
      <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>{isSticker ? "Sticker Order" : "Custom Project"}</div>
      <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-none" style={{color: "var(--primary)"}}>
        {isSticker ? "Just your details, then design." : "Tell us your story."}
      </h1>
      <p className="mt-4 max-w-xl text-[color:var(--text-muted)]">
        {isSticker
          ? "Share your shipping details and artwork notes — payment runs on this page and we start production the same day."
          : "Submit your idea and we'll reply within 24 hours with a firm quote. Approve, save a card on file, and we only charge once we agree on price."}
      </p>

      {!isSticker && <QuoteProcess/>}

      {design && (
        <div data-testid="attached-design" className="mt-8 p-4 rounded-lg border hairline bg-[color:var(--muted)]/40 text-sm">
          A design from the Studio is attached to this order — <span className="font-semibold">{design.material}</span>, {design.layers.length} layer(s).
        </div>
      )}

      <form onSubmit={submit} data-testid="custom-order-form" className="mt-10 bg-white rounded-lg border hairline p-8 space-y-6">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Full name</Label>
            <Input data-testid="order-name" required value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})}/>
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="order-email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/>
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="order-phone" required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/>
          </div>
          <div>
            <Label>Quantity</Label>
            <Input data-testid="order-quantity" type="number" min={1} value={form.quantity} onChange={(e) => setForm({...form, quantity: parseInt(e.target.value)||1})}/>
          </div>
        </div>

        <div>
          <Label>Shipping address</Label>
          <Textarea data-testid="order-address" required rows={2} value={form.shipping_address} onChange={(e) => setForm({...form, shipping_address: e.target.value})}/>
        </div>

        <ProjectFields isSticker={isSticker} form={form} setForm={setForm}/>

        <div>
          <Label>Detailed description — engraving text, size, style, references</Label>
          <Textarea data-testid="order-description" required rows={5} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder={isSticker ? "e.g. '3-inch die-cut logo stickers, matte finish, exactly like the artwork I uploaded.'" : "e.g. 'Cherry wood plaque 8x10, engrave the poem in italic serif, name in large sans-serif above.'"}/>
        </div>

        <ToggleCard testid="military-block" toggleTestid="military-toggle" checked={form.military_discount}
          onToggle={() => setForm({...form, military_discount: !form.military_discount})} icon={ShieldCheck} title="Military & First Responder — 10% off"
          aside={isSticker && (
            <div className="text-right shrink-0">
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Discount</div>
              <div className="font-serif text-xl" style={{color: "var(--primary)"}}>−${militaryOff}</div>
            </div>
          )}>
          Active duty, veteran, fire/EMS/police, and <span className="font-semibold">immediate family members</span> qualify.
          We'll request simple proof after you submit — once verified the discount is guaranteed.
        </ToggleCard>

        {isSticker && (
          <ToggleCard testid="rush-toggle-block" toggleTestid="rush-toggle" checked={form.rush} onToggle={() => setForm({...form, rush: !form.rush})}
            icon={Zap} title="Need it in 48 hours?"
            aside={(
              <div className="text-right shrink-0">
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">Rush fee</div>
                <div className="font-serif text-2xl" style={{color: "var(--primary)"}}>+${RUSH_FEE}</div>
              </div>
            )}>
            Rush production and shipping — completed and out the door in under 2 business days.
          </ToggleCard>
        )}

        <OrderTotal isSticker={isSticker} total={total} form={form} submitting={submitting}/>
      </form>
    </div>
  );
}
